import { useTranslation } from 'react-i18next';
import { LayoutTemplate, Pencil, Download, type LucideIcon } from 'lucide-react';

type Step = {
  num: string;
  icon: LucideIcon;
  titleKey: string;
  descKey: string;
  numGradient: string;
  iconClass: string;
  glow: string;
};

const STEPS: Step[] = [
  {
    num: '01',
    icon: LayoutTemplate,
    titleKey: 'home.steps.step1Title',
    descKey: 'home.steps.step1Desc',
    numGradient: 'from-violet-500 to-violet-600',
    iconClass: 'bg-violet-500/15 text-violet-600 ring-violet-500/25 dark:text-violet-300',
    glow: 'shadow-violet-500/20',
  },
  {
    num: '02',
    icon: Pencil,
    titleKey: 'home.steps.step2Title',
    descKey: 'home.steps.step2Desc',
    numGradient: 'from-sky-500 to-sky-600',
    iconClass: 'bg-sky-500/15 text-sky-600 ring-sky-500/25 dark:text-sky-300',
    glow: 'shadow-sky-500/20',
  },
  {
    num: '03',
    icon: Download,
    titleKey: 'home.steps.step3Title',
    descKey: 'home.steps.step3Desc',
    numGradient: 'from-emerald-500 to-emerald-600',
    iconClass: 'bg-emerald-500/15 text-emerald-600 ring-emerald-500/25 dark:text-emerald-300',
    glow: 'shadow-emerald-500/20',
  },
];

export function HowItWorks() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden border-t border-border bg-gradient-to-br from-violet-50/70 via-white to-sky-50/70 dark:from-violet-950/30 dark:via-slate-950/40 dark:to-sky-950/30">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/4 top-1/3 size-[420px] -translate-x-1/2 rounded-full bg-violet-400/20 blur-[120px] dark:bg-violet-500/15" />
        <div className="absolute bottom-1/4 right-1/4 size-[460px] translate-x-1/2 rounded-full bg-sky-400/20 blur-[120px] dark:bg-sky-500/15" />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 py-16 md:py-24">
        <div className="mx-auto mb-12 max-w-2xl text-center md:mb-16">
          <p className="text-sm font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400">
            {t('home.steps.eyebrow')}
          </p>
          <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight md:text-4xl">
            {t('home.steps.title')}
          </h2>
          <p className="mt-3 text-base text-muted-foreground md:text-lg">
            {t('home.steps.subtitle')}
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {STEPS.map((step) => {
            const Icon = step.icon;
            return (
              <div
                key={step.num}
                className="group relative flex flex-col gap-4 rounded-2xl border border-border/60 bg-white/60 p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-border hover:shadow-xl dark:bg-card/60"
              >
                <span
                  className={`bg-gradient-to-br ${step.numGradient} bg-clip-text text-7xl font-extrabold leading-none text-transparent`}
                >
                  {step.num}
                </span>
                <div
                  className={`inline-flex size-12 items-center justify-center rounded-xl shadow-lg ring-1 ${step.iconClass} ${step.glow}`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold">{t(step.titleKey)}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {t(step.descKey)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
